'use client';

import { AlertOctagon, RefreshCw } from 'lucide-react';
import React from 'react';

import { cn } from '../../utils/cn';
import { UzButton } from '../form/UzButton';

import type { HTMLAttributes, ReactNode } from 'react';

export interface UzErrorStateProps extends Omit<HTMLAttributes<HTMLDivElement>, 'title'> {
  title?: ReactNode;
  description?: ReactNode;
  /** Optional retry handler. Renders a retry button when provided. */
  onRetry?: () => void;
  /** Custom action node, used in place of the retry button. */
  action?: ReactNode;
  /** Inspect-only details (error.message, stack). Hidden by default. */
  detail?: string;
  /** Visual density. Compact for inline use. */
  size?: 'default' | 'compact';
}

export const UzErrorState = ({
  title = 'Something went wrong',
  description = 'Please try again. If the problem continues, contact support.',
  onRetry,
  action,
  detail,
  size = 'default',
  className,
  ...rest
}: UzErrorStateProps): React.JSX.Element => (
  <div
    role="alert"
    className={cn(
      'flex flex-col items-center justify-center text-center',
      size === 'default' && 'gap-3 py-12',
      size === 'compact' && 'gap-2 py-6',
      className,
    )}
    {...rest}
  >
    <AlertOctagon className="text-destructive h-8 w-8" />
    <h3 className="font-heading text-base font-semibold">{title}</h3>
    {description && <p className="text-muted-foreground max-w-md text-sm">{description}</p>}
    {(action || onRetry) && (
      <div className="mt-2 flex items-center gap-2">
        {action ?? (
          <UzButton
            variant="outline"
            leftIcon={<RefreshCw className="h-4 w-4" />}
            onClick={onRetry}
          >
            Retry
          </UzButton>
        )}
      </div>
    )}
    {detail && (
      <details className="text-muted-foreground mt-3 max-w-md text-left text-xs">
        <summary className="cursor-pointer">Technical details</summary>
        <pre className="bg-muted mt-2 overflow-auto rounded p-2">{detail}</pre>
      </details>
    )}
  </div>
);
